import { useState, useEffect } from 'react';

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Session beim Start laden
  useEffect(() => {
    const loadStoredSession = async () => {
      try {
        const storedUser = localStorage.getItem('sap_oci_user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          
          // Token-Gültigkeit prüfen
          if (userData.token && isTokenValid(userData.token)) {
            console.log('Stored session found and valid, restoring user');
            setUser(userData);
          } else {
            console.log('Stored session expired, clearing storage');
            localStorage.removeItem('sap_oci_user');
          }
        }
      } catch (error) {
        console.error('Error loading stored session:', error);
        localStorage.removeItem('sap_oci_user');
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredSession();
  }, []);

  // User-State direkt aktualisieren
  const updateUser = (updatedUser) => {
    const newUser = { ...user, ...updatedUser };
    setUser(newUser);
    try {
      localStorage.setItem('sap_oci_user', JSON.stringify(newUser));
    } catch (error) {
      console.error('Error updating user in localStorage:', error);
    }
  };

  // Token-Gültigkeit prüfen
  const isTokenValid = (token) => {
    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      // Prüfen ob Token noch nicht abgelaufen ist (mit 5 Minuten Puffer)
      return tokenPayload.exp > (currentTime + 300);
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  };

  const handleLogin = async (userData) => {
    try {
      console.log('Attempting login with:', { username: userData.username, password: '[HIDDEN]' });
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: userData.username,
          password: userData.password
        })
      });
      
      const loginData = await response.json();
      
      if (!response.ok) {
        throw new Error(loginData.error || `Login failed: ${response.status}`);
      }
      
      // Backend Response Format:
      // {
      //   "token": "jwt_token_here",
      //   "user": {
      //     "userId": 123,
      //     "username": "admin",
      //     "name": "Administrator", 
      //     "costCenter": "ADMIN",
      //     "role": "admin"
      //   }
      // }
      // 
      // User-ID aus Backend Response oder JWT-Token extrahieren
      let userId = loginData.user.userId; // Direkt aus Backend Response
      
      // Fallback: Aus JWT-Token extrahieren falls nicht in Response
      if (!userId) {
        try {
          const tokenPayload = JSON.parse(atob(loginData.token.split('.')[1]));
          userId = tokenPayload.userId;
        } catch (e) {
          console.warn('Could not extract userId from token');
        }
      }
      
      const userWithToken = {
        id: userId, // Aus Backend Response oder JWT-Token extrahiert
        username: loginData.user.username,
        firstName: loginData.user.firstName,
        lastName: loginData.user.lastName,
        email: loginData.user.email,
        department: loginData.user.department,
        plant: loginData.user.plant,
        name: loginData.user.name || loginData.user.username, // Fallback
        costCenter: loginData.user.costCenter || 'DEFAULT',
        purchasingOrg: loginData.user.purchasingOrg,
        purchasingGroup: loginData.user.purchasingGroup || '',
        sapUser: loginData.user.sapUser || '',
        requestor: loginData.user.requestor || '',
        role: loginData.user.role,
        token: loginData.token,
        logo: loginData.user.logo || null,
        loginTime: Date.now() // Für Session-Tracking
      };
      
      console.log('🐛 Debug: Login successful with userId:', userId, 'from backend/token');
      console.log('Login successful:', { ...userWithToken, token: '[HIDDEN]' });
      
      // Session in localStorage speichern
      try {
        localStorage.setItem('sap_oci_user', JSON.stringify(userWithToken));
        console.log('Session saved to localStorage');
      } catch (storageError) {
        console.warn('Could not save session to localStorage:', storageError);
      }
      
      setUser(userWithToken);
      
    } catch (error) {
      console.error('Login error:', error);
      
      // Spezifische Fehlermeldungen basierend auf Backend-Response
      let errorMessage = 'Anmeldung fehlgeschlagen.';
      
      if (error.message.includes('fetch') || error.message.includes('NetworkError')) {
        errorMessage = 'Verbindungsfehler: Kann nicht mit dem Server verbinden.';
      } else if (error.message.includes('Administrator-Passwort')) {
        errorMessage = 'Falsches Administrator-Passwort.';
      } else if (error.message.includes('nicht gefunden')) {
        errorMessage = 'Benutzer nicht gefunden oder deaktiviert.';
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        errorMessage = 'Ungültige Anmeldedaten. Bitte überprüfen Sie Benutzername und Passwort.';
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        errorMessage = 'Zugriff verweigert. Ihr Account ist möglicherweise deaktiviert.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };

  const handleLogout = async (cart = [], ociMode = false, ociParams = null) => {
    // Spezielle Behandlung für OCI-Benutzer
    if (ociMode && ociParams) {
      console.log('🚀 OCI-Logout: Bereite SAP-Rückkehr vor');
      
      // Für OCI-Benutzer: Direkt zurück zu SAP ohne Bestätigung
      try {
        await performOciLogout(ociParams);
        
        // Session lokal löschen
        localStorage.removeItem('sap_oci_user');
        
        // Clear all BANF draft data for all users
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('banf_draft_')) {
            localStorage.removeItem(key);
          }
        });
        
        setUser(null);
        
        console.log('✅ OCI-Logout erfolgreich - Rückkehr zu SAP');
        return true;
      } catch (error) {
        console.error('❌ OCI-Logout fehlgeschlagen:', error);
        // Fallback: Normaler Logout
      }
    }
    
    // Normale Logout-Bestätigung wird jetzt über ein Modal in der App-Komponente gehandhabt
    // Diese Funktion wird nur noch aufgerufen, wenn die Bestätigung bereits erfolgt ist
    
    try {
      if (user?.token) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('Logout API call successful');
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Logout trotzdem durchführen, auch wenn API-Call fehlschlägt
    }
    
    // Session aus localStorage entfernen
    try {
      localStorage.removeItem('sap_oci_user');
      
      // Clear all BANF draft data for all users
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('banf_draft_')) {
          localStorage.removeItem(key);
        }
      });
      
      console.log('Session and BANF drafts removed from localStorage');
    } catch (storageError) {
      console.warn('Could not remove session from localStorage:', storageError);
    }
    
    setUser(null);
    return true;
  };
  
  // OCI-spezifische Logout-Funktion
  const performOciLogout = async (ociParams) => {
    console.log('🔄 Führe OCI-Logout durch - Rückkehr zu SAP mit leeren Daten');
    
    // Prüfe ob SAP-Integration
    const isSapIntegration = ociParams?.hookUrl && (
      ociParams.hookUrl.includes('sap-cust://') || 
      ociParams.hookUrl.includes('SAPEVENT:') ||
      ociParams.hookUrl.includes('sap-place-holder')
    );
    
    if (isSapIntegration) {
      console.log('🚀 SAP-Integration erkannt - bereite sofortige Rückkehr vor');
      
      // Erstelle OCI-Form für SAP-Rückkehr mit leeren Daten
      const ociForm = document.createElement('form');
      ociForm.method = 'POST';
      ociForm.action = ociParams.hookUrl;
      ociForm.target = '_self'; // Im gleichen Fenster - kehrt zu SAP zurück
      ociForm.style.display = 'none';
      
      // SAP-Logout-Parameter (mit leeren Warenkorb-Daten)
      const logoutParams = [
        { name: '~OkCode', value: 'CNCL' }, // Cancel - wichtig für Logout
        { name: '~TARGET', value: '_self' },
        { name: '~CALLER', value: 'ProCureX' },
        { name: '~CLOSE_CATALOG', value: 'TRUE' },
        { name: '~RETURN_URL', value: 'BACK' },
        { name: 'NEW_ITEM-COUNT', value: '0' }, // Keine Artikel übertragen
        { name: 'HOOKLOGOUT', value: 'TRUE' } // Logout-Flag
      ];
      
      logoutParams.forEach(param => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = param.name;
        input.value = param.value;
        ociForm.appendChild(input);
      });
      
      // Form zur Seite hinzufügen und SOFORT submitten
      document.body.appendChild(ociForm);
      
      console.log('📤 Submitte OCI-Logout-Form SOFORT zur SAP-Hook-URL:', ociParams.hookUrl);
      try {
        ociForm.submit();
        console.log('✅ OCI-Logout erfolgreich an SAP gesendet - Browser kehrt zu SAP zurück!');
      } catch (error) {
        console.log('⚠️ OCI-Logout Form-Submit fehlgeschlagen:', error);
        // Fallback: Browser-Fenster schließen
        if (window.opener) {
          window.close();
        } else {
          // Fallback: Direkte Navigation zur Hook-URL
          window.location.href = ociParams.hookUrl;
        }
      }
      
    } else {
      // Für normale URLs: Browser-Fenster schließen oder Hook-URL aufrufen
      console.log('🔄 Standard OCI-Logout: Browser-Fenster schließen');
      
      if (window.opener) {
        // Pop-up-Fenster schließen
        window.close();
      } else if (ociParams.hookUrl) {
        // Zurück zur Hook-URL mit leeren Daten
        window.location.href = ociParams.hookUrl;
      } else {
        // Fallback: Browser-Fenster schließen
        window.close();
      }
    }
  };

  return {
    user,
    isLoading,
    handleLogin,
    handleLogout,
    updateUser
  };
};

export default useAuth;